import type { CoverageState } from './types';

export interface Span {
    state: CoverageState;
    fromMono: number;
    toMono: number;
}

export interface TickContext {
    hasSession: boolean;
    busy: boolean;
}

const STALL_FACTOR = 1.5;

/**
 * Decides per tick whether to send a probe and how to account the time since the previous tick
 * (spec 4.2). Missed ticks become stalled time; they are never caught up with a burst.
 */
export class Pacer {
    private last?: number;

    constructor(private readonly intervalMs: number) {}

    tick(nowMono: number, ctx: TickContext): { spans: Span[]; send: boolean } {
        const from = this.last ?? nowMono - this.intervalMs;
        // Queued ticks arriving back to back: wait for the next real one, which covers this time.
        if (nowMono - from < this.intervalMs / 2) return { spans: [], send: false };
        this.last = nowMono;
        if (!ctx.hasSession) return { spans: [{ state: 'noSession', fromMono: from, toMono: nowMono }], send: false };
        const spans: Span[] = [];
        let current = from;
        if (nowMono - from > STALL_FACTOR * this.intervalMs) {
            current = nowMono - this.intervalMs;
            spans.push({ state: 'stalled', fromMono: from, toMono: current });
        }
        if (ctx.busy) {
            spans.push({ state: 'blocked', fromMono: current, toMono: nowMono });
            return { spans, send: false };
        }
        spans.push({ state: 'measured', fromMono: current, toMono: nowMono });
        return { spans, send: true };
    }
}
